import type { DrawFn } from "./math";
import { createSpark } from "../fx/spark";
import { createPulse } from "../fx/pulse";
import { createVoltage } from "../fx/voltage";
import { createMercury } from "../fx/mercury";
import { createPrism } from "../fx/prism";
import { createVeil } from "../fx/veil";
import { createOrbit } from "../fx/orbit";
import { createPhoenix } from "../fx/phoenix";
import { createSovereign } from "../fx/sovereign";
import { createApex } from "../fx/apex";
import { createAfterglow } from "../fx/afterglow";
import { createOrigin } from "../fx/origin";
import { createReignite } from "../fx/reignite";

export type RitualId =
  | "spark"
  | "pulse"
  | "voltage"
  | "mercury"
  | "prism"
  | "veil"
  | "orbit"
  | "phoenix"
  | "sovereign"
  | "apex"
  | "afterglow"
  | "origin"
  | "reignite"
  | "forge"
  | "tonnage"
  | "lattice"
  | "almanac"
  | "still"
  | "oath"
  | "nocturne"
  | "breath";

export interface RitualDef {
  id: RitualId;
  plate: string;
  accent: string;
  plateOpacity: number;
  fx: () => DrawFn;
  minWeeks?: number;
}

export const STREAK_RITUALS: RitualDef[] = [
  {
    id: "apex",
    minWeeks: 52,
    plate: "/plates/apex.jpg",
    accent: "#f2e6c4",
    plateOpacity: 0.7,
    fx: createApex,
  },
  {
    id: "sovereign",
    minWeeks: 39,
    plate: "/plates/sovereign.jpg",
    accent: "#e8c872",
    plateOpacity: 0.62,
    fx: createSovereign,
  },
  {
    id: "phoenix",
    minWeeks: 26,
    plate: "/plates/phoenix.jpg",
    accent: "#ff7a3a",
    plateOpacity: 0.58,
    fx: createPhoenix,
  },
  {
    id: "orbit",
    minWeeks: 16,
    plate: "/plates/orbit.jpg",
    accent: "#e8c872",
    plateOpacity: 0.5,
    fx: createOrbit,
  },
  {
    id: "veil",
    minWeeks: 12,
    plate: "/plates/veil.jpg",
    accent: "#5cffc0",
    plateOpacity: 0.46,
    fx: createVeil,
  },
  {
    id: "prism",
    minWeeks: 8,
    plate: "/plates/prism.jpg",
    accent: "#c9e8ff",
    plateOpacity: 0.5,
    fx: createPrism,
  },
  {
    id: "mercury",
    minWeeks: 4,
    plate: "/plates/mercury.jpg",
    accent: "#d5dee8",
    plateOpacity: 0.48,
    fx: createMercury,
  },
  {
    id: "voltage",
    minWeeks: 3,
    plate: "/plates/voltage.jpg",
    accent: "#7ad7ff",
    plateOpacity: 0.42,
    fx: createVoltage,
  },
  {
    id: "pulse",
    minWeeks: 2,
    plate: "/plates/pulse.jpg",
    accent: "#ff6b4a",
    plateOpacity: 0.5,
    fx: createPulse,
  },
  {
    id: "spark",
    minWeeks: 0,
    plate: "/plates/spark.jpg",
    accent: "#ff5a1f",
    plateOpacity: 0.55,
    fx: createSpark,
  },
];

export const SCENE_PLATES = {
  calendar: { plate: "/plates/lattice.jpg", opacity: 0.28, accent: "#ff6b3a" },
  workouts: { plate: "/plates/almanac.jpg", opacity: 0.24, accent: "#e8c872" },
  stats: { plate: "/plates/tonnage.jpg", opacity: 0.28, accent: "#e8c872" },
  profile: { plate: "/plates/sovereign.jpg", opacity: 0.32, accent: "#e8c872" },
  exercises: { plate: "/plates/forge.jpg", opacity: 0.24, accent: "#ff7a2a" },
  weight: { plate: "/plates/still.jpg", opacity: 0.3, accent: "#c9d6ee" },
  templates: { plate: "/plates/oath.jpg", opacity: 0.26, accent: "#e8c872" },
  admin: { plate: "/plates/nocturne.jpg", opacity: 0.3, accent: "#e8b56a" },
  active: { plate: "/plates/forge.jpg", opacity: 0.34, accent: "#ff7a2a" },
  login: { plate: "/plates/origin.jpg", opacity: 0.62, accent: "#ffb347" },
  editor: { plate: "/plates/mark.jpg", opacity: 0.22, accent: "#e8c872" },
  detail: { plate: "/plates/afterglow.jpg", opacity: 0.26, accent: "#ff6b4a" },
} as const;

export const AFTERGLOW: RitualDef = {
  id: "afterglow",
  plate: "/plates/afterglow.jpg",
  accent: "#ff6b4a",
  plateOpacity: 0.62,
  fx: createAfterglow,
};

export const ORIGIN: RitualDef = {
  id: "origin",
  plate: "/plates/origin.jpg",
  accent: "#ffb347",
  plateOpacity: 0.62,
  fx: createOrigin,
};

export const REIGNITE: RitualDef = {
  id: "reignite",
  plate: "/plates/reignite.jpg",
  accent: "#ff5a1f",
  plateOpacity: 0.7,
  fx: createReignite,
};

export function resolveStreakRitual(weeks: number): RitualDef {
  const n = Math.max(0, Math.floor(weeks));
  for (const ritual of STREAK_RITUALS) {
    if (n >= (ritual.minWeeks ?? 0)) return ritual;
  }
  return STREAK_RITUALS[STREAK_RITUALS.length - 1]!;
}

export function sceneForPath(pathname: string): {
  plate: string;
  opacity: number;
  accent: string;
} {
  if (pathname.startsWith("/workouts/active")) return SCENE_PLATES.active;
  if (pathname.startsWith("/workouts/completed") || pathname.startsWith("/workouts/planned")) {
    if (pathname.includes("/edit") || pathname.includes("/new/")) return SCENE_PLATES.editor;
    return SCENE_PLATES.detail;
  }
  if (pathname.startsWith("/workouts")) return SCENE_PLATES.workouts;
  if (pathname.startsWith("/stats")) return SCENE_PLATES.stats;
  if (pathname.startsWith("/profile")) return SCENE_PLATES.profile;
  if (pathname.startsWith("/exercises")) return SCENE_PLATES.exercises;
  if (pathname.startsWith("/weight")) return SCENE_PLATES.weight;
  if (pathname.startsWith("/templates")) return SCENE_PLATES.templates;
  if (pathname.startsWith("/admin")) return SCENE_PLATES.admin;
  return SCENE_PLATES.calendar;
}
